// ============================================================================
// shared/parcours.ts — LE PARCOURS : mondes, ordre, déverrouillage
// ----------------------------------------------------------------------------
// Module PUR : ni DOM, ni canvas, ni base, ni Date.now, ni Math.random. Le
// client l'utilise pour DESSINER la carte, le serveur pour DÉCIDER ce qui est
// ouvert. Une seule vérité, donc aucune divergence possible entre les deux.
//
// ⚠️ IL REMPLACE `PARCOURS_ORDER` (shared/levels-catalog), qui n'était qu'une
// liste plate. On garde cette liste : elle reste l'ordre du mode libre et du
// sélecteur, et trois tests la verrouillent. Ici on ajoute ce qu'une liste ne
// sait pas dire — les mondes, les boss, et ce qui ouvre quoi.
//
// ⚠️ LE DÉVERROUILLAGE SE CALCULE CÔTÉ SERVEUR. `etatDuNoeud` est exporté et
// utilisable au client pour l'affichage, mais c'est le serveur qui refuse un
// score sur un niveau non ouvert. Sinon un enfant curieux ouvre la console et
// finit le jeu en dix secondes.

import type { WorldKey } from './worlds'

export type GenreNoeud = 'normal' | 'boss' | 'bonus'
export type EtatNoeud = 'fini' | 'courant' | 'cle' | 'verrouille'

/** Nombre d'échecs sur un MÊME niveau avant qu'une clé de passage n'apparaisse.
 *  Choix de Claude-Alain (30/07) : bloquant, mais jamais coinçant. Un enfant de
 *  six ans ne doit pas rester bloqué des semaines — c'est exactement ce qui
 *  s'est passé avec « Grande carte », qui a arrêté six joueurs sur sept. */
export const ECHECS_AVANT_CLE = 5

export interface NoeudParcours {
  /** Le niveau joué. Slug du catalogue, ou `fam-<id>` pour une carte de la famille. */
  slug: string
  titre: string
  genre: GenreNoeud
}

export interface MondeParcours {
  cle: string
  nom: string
  /** Ce que le monde apprend, en une phrase, montrée au joueur. */
  lecon: string
  /** Le biome (shared/worlds) : décide du ciel et des décors. */
  monde: WorldKey
  /** Les mécaniques introduites ici. Sert la carte ET la relecture du design. */
  mecaniques: readonly string[]
  /** La couleur de ce monde sur la carte.
   *  ⚠️ Choisie À LA MAIN, et surtout PAS reprise du `motifColor` du biome : celui du monde
   *  classique vaut #0d0b1a, exactement le fond de l'écran — le libellé « Monde 1 » sortait
   *  invisible. Un accent doit se lire sur le fond où il sera posé, pas décrire le décor. */
  accent: string
  /** Le loot offert en finissant le boss. `null` tant qu'il n'est pas dessiné. */
  recompense: string | null
  noeuds: readonly NoeudParcours[]
}

const nv = (slug: string, titre: string): NoeudParcours => ({ slug, titre, genre: 'normal' })
const bs = (slug: string, titre: string): NoeudParcours => ({ slug, titre, genre: 'boss' })

/**
 * LES MONDES.
 *
 * ⚠️ Seuls les nœuds dont le `slug` existe VRAIMENT sont listés ici. Le parcours
 * se remplit monde par monde, au fur et à mesure que les niveaux sont écrits et
 * joués : un nœud qui pointe vers un slug inconnu ferait planter le sélecteur,
 * et un test le refuse. Les mondes 3 à 8 sont donc décrits (nom, leçon,
 * mécaniques, récompense) mais encore vides — la carte les montre « à venir »
 * plutôt que de mentir avec des pastilles verrouillées qui n'ouvriront jamais.
 *
 * Les neuf étapes existantes sont réparties selon ce qu'elles ENSEIGNENT
 * réellement, pas selon leur numéro : « Glace et sable » ouvre le monde 2 parce
 * que c'est là qu'on apprend à glisser.
 */
export const MONDES: readonly MondeParcours[] = [
  {
    cle: 'chantier', accent: '#e0a94a', nom: 'Le chantier', monde: 'classique',
    lecon: 'Creuser, grimper, s’accrocher.',
    mecaniques: ['brique', 'échelle', 'corde', 'fausse brique'],
    recompense: null,
    noeuds: [
      nv('01-premiers-pas', 'Premiers pas'),
      nv('02-la-corde-raide', 'La corde raide'),
      nv('03-creuse', 'Creuse !'),
      // LA MARCHE QUI MANQUAIT (30/07). Le boss du monde s'appelle « Les moines fous » et se
      // gagne en PIÉGEANT les moines dans un trou creusé. Aucune étape avant ne le demandait :
      // on apprenait à creuser (03) puis on affrontait deux moines malins d'un coup.
      nv('c1-le-piege', 'Le piège'),
      bs('05-les-moines-fous', 'Les moines fous'),
    ],
  },
  {
    cle: 'glissades', accent: '#8fd0f0', nom: 'Glace et sable', monde: 'neige',
    lecon: 'Le sol ne te retient pas toujours.',
    mecaniques: ['glace', 'sable', 'moine rapide'],
    recompense: null,
    noeuds: [
      // LA GLACE SEULE, PUIS LE SABLE SEUL (30/07), avant la carte qui les mélange. « Glace et
      // sable » ouvrait le monde en lâchant les deux matières, un marteau et deux moines dans
      // la même grille. Elle reste, mais à sa place : la synthèse, pas l'introduction.
      // Personne ne perd sa progression — `finis` se déduit des scores déjà enregistrés, donc
      // un joueur qui a déjà fini le 04 le garde acquis.
      nv('g1-patinoire', 'Patinoire'),
      nv('g2-sables-mouvants', 'Sables mouvants'),
      nv('04-glace-et-sable', 'Glace et sable'),
      nv('06-arene-face-a-face', 'Arène : face à face'),
      bs('07-arene-souterraine', 'Arène : souterraine'),
    ],
  },
  {
    cle: 'usine', accent: '#7fd4e8', nom: 'L’usine', monde: 'usine',
    lecon: 'Ici, le décor bouge tout seul.',
    mecaniques: ['tapis roulant', 'monte-charge', 'ressort', 'rampe', 'sol qui s’effrite'],
    recompense: null,
    noeuds: [
      // LE CINQUIÈME MONDE (31/07). Le moteur sait faire une trentaine de mécaniques et le
      // parcours n'en montrait qu'une poignée : tapis, conduits, ressorts et rampes dormaient,
      // réservés à une seule grande carte bonus que presque personne n'atteignait.
      nv('u1-tapis-roulant', 'Le tapis roulant'),
      nv('u2-le-monte-charge', 'Le monte-charge'),
      nv('u3-les-ressorts', 'Les ressorts'),
      nv('u4-la-chaine', 'La chaîne de montage'),
      bs('u5-le-broyeur', 'Le broyeur'),
    ],
  },
  {
    cle: 'mirages', accent: '#f0b45c', nom: 'Les mirages', monde: 'desert',
    lecon: 'Entre ici, ressors là-bas.',
    mecaniques: ['téléporteur', 'béton', 'sable', 'pics'],
    recompense: null,
    noeuds: [
      // LE SIXIÈME MONDE (31/07). Le téléporteur existait dans le moteur depuis le début et
      // n'avait jamais servi à rien : deux cases oubliées dans « Les moines fous ».
      nv('t1-le-passage', 'Le passage'),
      nv('t2-deux-portes', 'Deux portes'),
      nv('t3-le-puits', 'Le puits'),
      nv('t4-le-labyrinthe', 'Le labyrinthe'),
      bs('t5-le-mirage', 'Le mirage'),
    ],
  },
  {
    cle: 'serrures', accent: '#9d8cf0', nom: 'Les serrures', monde: 'grotte',
    lecon: 'Ce qui est fermé s’ouvre, si tu trouves la clé.',
    mecaniques: ['porte', 'clé', 'sol qui s’effrite', 'lave'],
    recompense: null,
    noeuds: [
      // LE SEPTIÈME MONDE (31/07). Portes et clés dormaient dans le moteur depuis les tuiles
      // Phase 3 sans qu'aucun niveau du parcours ne les emploie.
      nv('s1-la-cle', 'La clé'),
      nv('s2-deux-serrures', 'Deux serrures'),
      nv('s3-le-trousseau', 'La cellule'),
      nv('s4-le-donjon', 'Le donjon'),
      bs('s5-le-gardien', 'Le gardien'),
    ],
  },
  {
    cle: 'ferme', accent: '#9fd45c', nom: 'La grande ferme', monde: 'prairie',
    lecon: 'Certains passages ne s’ouvrent que dans un sens.',
    mecaniques: ['portillon à sens unique', 'porte', 'ressort', 'sol qui s’effrite'],
    recompense: null,
    noeuds: [
      // LE HUITIÈME ET DERNIER MONDE (31/07). Le sens unique existait depuis les tuiles du 17/07
      // et aucun niveau du parcours ne s'en servait. `prairie` était le seul biome inemployé.
      nv('f1-le-portillon', 'Le portillon'),
      nv('f2-le-detour', 'Le détour'),
      nv('f3-l-enclos', 'L’enclos'),
      nv('f4-le-troupeau', 'Le troupeau'),
      bs('f5-le-grand-pre', 'Le grand pré'),
    ],
  },
  {
    cle: 'profondeurs', accent: '#57d3c8', nom: 'Les profondeurs', monde: 'abysses',
    lecon: 'Sous l’eau, le temps compte double.',
    mecaniques: ['eau', 'souffle', 'source de bulles'],
    recompense: null,
    noeuds: [
      // LES QUATRE MARCHES QUI MANQUAIENT (30/07). Mesuré avant de les écrire : cinq joueurs
      // finissaient l'arène souterraine, UN SEUL l'épave. Il n'y avait rien entre les deux.
      // Chacune n'ajoute qu'une idée : nager, gérer son souffle, descendre, tout combiner.
      nv('a1-premiere-apnee', 'Première apnée'),
      nv('a2-de-bulle-en-bulle', 'De bulle en bulle'),
      nv('a3-le-siphon', 'Le siphon'),
      nv('a4-plus-profond', 'Plus profond'),
      // L'épave reste le boss : 208 s de record, un seul joueur l'a finie.
      bs('09-epave-engloutie', 'L’épave engloutie'),
    ],
  },
  {
    cle: 'manoir', accent: '#e0605a', nom: 'Le dernier étage', monde: 'manoir',
    lecon: 'Ici, les moines sont armés.',
    mecaniques: ['lave', 'feu', 'moine armé', 'bombe'],
    recompense: null,
    noeuds: [
      // QUATRE MARCHES AVANT LE MANOIR (30/07). Un seul joueur l'avait fini : rien n'apprenait
      // la lave, le feu ni les moines armés, on les découvrait tous d'un coup au dernier niveau.
      nv('m1-coulees', 'Coulées'),
      nv('m2-le-marteau', 'Le marteau'),
      nv('m3-meche-courte', 'Mèche courte'),
      nv('m4-face-a-face', 'Face à face'),
      bs('10-manoir-hante', 'Le manoir hanté'),
    ],
  },
  {
    cle: 'foire', accent: '#f07ad0', nom: 'La foire aux farces', monde: 'prairie',
    lecon: 'Ramasse l’objet : il change les règles.',
    mecaniques: ['maillet', 'déguisement', 'drapeau-crapaud', 'couronne', 'bandelette', 'potion de géant'],
    recompense: null,
    noeuds: [
      // LA NEUVIÈME ÎLE (13/08). Les neuf objets du versus dormaient dans les arènes de duel :
      // aucune étape du parcours n'en montrait un seul. Six marchent seul contre des moines,
      // et c'est exactement ce qu'une île enseigne — un objet par carte, la synthèse au boss.
      nv('x1-la-masse', 'La masse'),
      nv('x2-le-faux-moine', 'Le faux moine'),
      nv('x3-le-drapeau', 'Le drapeau'),
      nv('x4-la-couronne', 'La couronne'),
      bs('x5-la-grande-farce', 'La grande farce'),
    ],
  },
]

/** Tous les nœuds, à plat, dans l'ordre du parcours. */
export const NOEUDS: readonly NoeudParcours[] = MONDES.flatMap((m) => m.noeuds)

/** Le monde qui contient ce slug, ou undefined. */
export function mondeDuNoeud(slug: string): MondeParcours | undefined {
  return MONDES.find((m) => m.noeuds.some((n) => n.slug === slug))
}

/** Ce que le serveur sait du joueur. `echecs` ne compte que les morts/abandons. */
export interface EtatJoueur {
  finis: ReadonlySet<string>
  echecs: Readonly<Record<string, number>>
}

/**
 * L'état d'un nœud pour ce joueur. FONCTION PURE — c'est LA règle du parcours,
 * isolée pour être testée sans base ni DOM, et partagée client/serveur.
 *
 * L'ordre de test compte :
 *  1. déjà fini -> `fini`, quoi qu'il arrive. Un niveau terminé le reste même si
 *     le parcours est réorganisé plus tard : personne ne perd un record.
 *  2. un BONUS (carte de la famille) est toujours ouvert : il ne bloque jamais
 *     et ne conditionne jamais la suite.
 *  3. sinon, on regarde l'étape NORMALE qui précède (les bonus sont sautés).
 *     Pas de précédent = premier niveau du jeu -> ouvert.
 *  4. précédent fini -> ouvert. Précédent échoué ECHECS_AVANT_CLE fois -> une
 *     clé de passage. Sinon -> verrouillé.
 */
export function etatDuNoeud(slug: string, e: EtatJoueur): EtatNoeud {
  if (e.finis.has(slug)) return 'fini'
  const i = NOEUDS.findIndex((n) => n.slug === slug)
  if (i < 0) return 'verrouille'
  if (NOEUDS[i]!.genre === 'bonus') return 'courant'
  let prec: NoeudParcours | undefined
  for (let k = i - 1; k >= 0; k--) {
    if (NOEUDS[k]!.genre !== 'bonus') { prec = NOEUDS[k]; break }
  }
  if (!prec) return 'courant'
  if (e.finis.has(prec.slug)) return 'courant'
  return (e.echecs[prec.slug] ?? 0) >= ECHECS_AVANT_CLE ? 'cle' : 'verrouille'
}

/** Vrai si le joueur a le droit de LANCER ce niveau (le serveur s'en sert). */
export function noeudJouable(slug: string, e: EtatJoueur): boolean {
  const s = etatDuNoeud(slug, e)
  return s === 'fini' || s === 'courant' || s === 'cle'
}

/** Le prochain niveau à jouer : le premier qui n'est pas fini. undefined = tout fini. */
export function prochainNoeud(e: EtatJoueur): NoeudParcours | undefined {
  return NOEUDS.find((n) => n.genre !== 'bonus' && !e.finis.has(n.slug))
}

/** Combien d'étapes finies sur combien, hors bonus. Sert l'en-tête de la carte. */
export function avancement(e: EtatJoueur): { finis: number; total: number } {
  const etapes = NOEUDS.filter((n) => n.genre !== 'bonus')
  return { finis: etapes.filter((n) => e.finis.has(n.slug)).length, total: etapes.length }
}
